<template>
  <v-sheet>
    <v-card-text>
      <h5 class="font-weight-bold mb-1 text-h5">
        Reset Password
      </h5>

      <v-scale-transition
        group
        hide-on-leave
      >
        <div
          v-if="isLoadingScreenVisible"
          class="align-center d-flex flex-column pt-4"
        >
          <v-progress-circular
            color="primary"
            indeterminate
            size="60"
          />
          <p
            class="pt-4"
          >
            Please wait
          </p>
        </div>

        <div
          v-if="isPasswordScreenVisible"
          key="request-screen"
        >
          <p
            class="py-3 text--secondary"
          >
            Please provide your new credentials
          </p>

          <v-form
            v-model="validForm"
            @submit.prevent
          >
            <PasswordField
              v-model="form.password"
              outlined
              label="New Password"
              prepend-inner-icon="mdi-key-chain"
              :disabled="isSubmitting"
              :rules="rules.password"
              @focus="isPasswordFocused = true"
              @blur="isPasswordFocused = false"
            />

            <PasswordField
              v-model="form.passwordConfirmation"
              outlined
              label="Repeat New Password"
              prepend-inner-icon="mdi-key-chain"
              :disabled="isSubmitting"
              :rules="rules.password"
            />

            <PasswordRequirementsChecklist
              v-if="isPasswordFocused"
              :password="form.password"
              class="mb-3"
            />

            <div
              class="d-flex justify-center mt-3"
            >
              <v-btn
                block
                text
                :disabled="!validForm"
                :loading="isSubmitting"
                @click="submitNewPassword"
              >
                <v-icon left>
                  mdi-email-lock
                </v-icon>
                Set New Password
              </v-btn>
            </div>
          </v-form>
        </div>

        <div
          v-if="isSuccessScreenVisible"
          key="success-screen"
        >
          <v-divider
            class="mb-5"
          />
          <p>Your new password has been set.</p>
          <p>You can now login into the platform with your new credentials.</p>
          <p>Thank you.</p>
          <div
            class="d-flex justify-center mt-3"
          >
            <v-btn
              text
              @click="closeScreen"
            >
              <v-icon
                left
              >
                mdi-arrow-left
              </v-icon>
              Login Page
            </v-btn>
          </div>
        </div>

        <div
          v-if="isErrorScreenVisible"
          key="error-screen"
          class="pt-4"
        >
          <v-alert
            border="left"
            outlined
            prominent
            type="error"
          >
            {{ errors }}
          </v-alert>

          <div
            class="d-flex justify-center mt-3"
          >
            <v-btn
              v-if="!disableBackButton"
              text
              @click="showPasswordScreen"
            >
              <v-icon
                left
              >
                mdi-arrow-left
              </v-icon>
              Back
            </v-btn>

            <v-spacer />

            <v-btn
              text
              @click="closeScreen"
            >
              Login Page
              <v-icon
                right
              >
                mdi-arrow-right
              </v-icon>
            </v-btn>
          </div>
        </div>
      </v-scale-transition>
    </v-card-text>
  </v-sheet>
</template>

<script>
import { capitalize } from 'lodash'
import { mapActions } from 'vuex'

import { alertHandler } from '@/alertHandler'
import { parseErrorResponseMessage } from '@/utilities'
import { rules } from '@/utilities/validation-rules'

import { USER_NAMESPACE } from '@/store/user'

import PasswordField from '@/components/Misc/PasswordField'
import PasswordRequirementsChecklist from '@/components/Misc/PasswordRequirementsChecklist'

const Screens = {
  ErrorScreen: 3,
  LoadingScreen: 0,
  PasswordScreen: 1,
  SuccessScreen: 2,
}

export default {
  name: 'ResetPassword',

  components: {
    PasswordField,
    PasswordRequirementsChecklist,
  },

  setup () {
    return {
      ...alertHandler(),
    }
  },

  data () {
    return {
      currentScreen: Screens.RequestScreen,
      disableBackButton: false,
      errors: undefined,
      form: {
        password: '',
        passwordConfirmation: '',
      },

      isPasswordFocused: false,
      isSubmitting: false,
      rules: {
        password: [
          rules.required,
          rules.passwordMinLength,
          rules.passwordHasNumber,
          rules.passwordHasSpecialChar,
          rules.passwordNoLeadingOrTrailingSpace,
        ],
      },

      validForm: false,
    }
  },

  computed: {
    isErrorScreenVisible () {
      return this.currentScreen === Screens.ErrorScreen
    },

    isLoadingScreenVisible () {
      return this.currentScreen === Screens.RequestScreen
    },

    isPasswordScreenVisible () {
      return this.currentScreen === Screens.PasswordScreen
    },

    isSuccessScreenVisible () {
      return this.currentScreen === Screens.SuccessScreen
    },
  },

  created () {
    this.showLoadingScreen()
    this.verifyToken()
  },

  methods: {
    capitalize,

    closeScreen () {
      if (Object.keys(this.$route.query).length) this.$router.push({ path: this.$route.path, query: {} })
      this.$emit('on-close')
    },

    parseErrorEntry (error) {
      if (Array.isArray(error)) return error.join(', ')
      return error
    },

    showErrorScreen () {
      this.currentScreen = Screens.ErrorScreen
    },

    showLoadingScreen () {
      this.currentScreen = Screens.LoadingScreen
    },

    showPasswordScreen () {
      this.isSubmitting = false
      this.currentScreen = Screens.PasswordScreen
    },

    showSuccessScreen () {
      this.currentScreen = Screens.SuccessScreen
    },

    submitNewPassword () {
      this.isSubmitting = true

      this.recoverUserAccount({
        password: this.form.password,
        passwordConfirmation: this.form.passwordConfirmation,
        token: this.$route.query.rtoken,
      }).
        then(_ => this.showSuccessScreen()).
        catch(error => {
          this.errors = parseErrorResponseMessage(error)
          this.showErrorScreen()
          this.isSubmitting = false
        })
    },

    verifyToken () {
      this.showLoadingScreen()
      this.disableBackButton = true

      this.verifyUserAccountRecoveryToken(this.$route.query.rtoken).
        then(_ => this.showPasswordScreen()).
        catch(error => {
          this.errors = parseErrorResponseMessage(error)
          this.showErrorScreen()
        })
    },

    ...mapActions(USER_NAMESPACE, [
      'recoverUserAccount',
      'verifyUserAccountRecoveryToken',
    ]),
  },
}
</script>
